"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, CircleX, X } from "lucide-react";
import { cn } from "@/lib/utils";
const ToastProvider = ToastPrimitives.Provider;
const ToastViewport = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Viewport>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>>(({
  className,
  ...props
}, ref) => <ToastPrimitives.Viewport ref={ref} className={cn("fixed bottom-safe-16 left-0 z-[100000] flex max-h-screen w-full flex-col items-center justify-end gap-2 p-4 sm:bottom-0 sm:items-start md:max-w-[420px]", className)} {...props} />);
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
const toastVariants = cva("group pointer-events-auto relative flex w-fit max-w-[calc(100vw-2rem)] items-start justify-start gap-2 overflow-hidden rounded-2xl border px-3 py-2.5 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full sm:w-full sm:max-w-none sm:rounded-md sm:p-4 sm:pr-6 sm:data-[state=open]:slide-in-from-bottom-full sm:data-[state=closed]:slide-out-to-left-full data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:translate-y-0 data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:translate-y-(--radix-toast-swipe-move-y) data-[swipe=move]:transition-none data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-bottom-full sm:data-[swipe=end]:slide-out-to-right-full", {
  variants: {
    variant: {
      default: "border border-zinc-600 sm:border-border bg-zinc-800 text-white  sm:bg-card sm:text-foreground",
      destructive: "border border-zinc-600 sm:border-border bg-zinc-800 text-white sm:border sm:bg-card sm:text-foreground",
      success: "border border-zinc-600 sm:border-border bg-zinc-800 text-white sm:border sm:bg-card sm:text-foreground",
      minimal: "p-4 pr-2 flex text-xs justify-center items-center text-center border-none bg-zinc-800/90 backdrop-blur-sm rounded-lg text-white w-auto fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 "
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Root>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>>(({
  className,
  variant,
  ...props
}, ref) => {
  const rootRef = React.useRef<HTMLLIElement>(null);
  const startYRef = React.useRef(0);
  const currentYRef = React.useRef(0);
  const isDismissingRef = React.useRef(false);
  const SWIPE_THRESHOLD = 30; // px to dismiss

  React.useEffect(() => {
    // Lock position when dismissing to prevent Radix from resetting it
    if (isDismissingRef.current && rootRef.current) {
      rootRef.current.style.transform = `translateY(500px)`;
      rootRef.current.style.transition = "transform 300ms ease-out";
    }
  });
  const handlePointerDown = (e: React.PointerEvent) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    if (isDismissingRef.current) return;
    startYRef.current = e.clientY;
    currentYRef.current = 0;
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    if (startYRef.current === 0 || isDismissingRef.current) return;
    const deltaY = e.clientY - startYRef.current;
    if (deltaY > 0 && rootRef.current) {
      currentYRef.current = deltaY;
      rootRef.current.style.transform = `translateY(${deltaY}px)`;
      rootRef.current.style.transition = "none";
    }
  };
  const handlePointerUp = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    if (isDismissingRef.current) return;
    if (currentYRef.current >= SWIPE_THRESHOLD) {
      isDismissingRef.current = true;
      if (rootRef.current) {
        rootRef.current.style.transform = `translateY(500px)`;
        rootRef.current.style.transition = "transform 300ms ease-out";
      }
      setTimeout(() => {
        props.onOpenChange?.(false);
      }, 350);
    } else if (rootRef.current) {
      rootRef.current.style.transform = "";
      rootRef.current.style.transition = "transform 200ms ease-out";
    }
    startYRef.current = 0;
    currentYRef.current = 0;
  };
  return <ToastPrimitives.Root ref={node => {
    rootRef.current = node;
    if (typeof ref === "function") ref(node);else if (ref) ref.current = node;
  }} className={cn(toastVariants({
    variant
  }), isDismissingRef.current && "![animation:none]", className)} data-variant={variant ?? "default"} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} {...props}>
      {variant === "success" && <Check className="h-6 w-6 text-success shrink-0 mt-1" />}
      {variant === "destructive" && <CircleX className="h-6 w-6 text-danger shrink-0 mt-1" />}
      {props.children}
    </ToastPrimitives.Root>;
});
Toast.displayName = ToastPrimitives.Root.displayName;
const ToastAction = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Action>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>>(({
  className,
  ...props
}, ref) => <ToastPrimitives.Action ref={ref} className={cn("inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-hidden focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 hover:group-[.destructive]:border-destructive/30 hover:group-[.destructive]:bg-destructive hover:group-[.destructive]:text-destructive-foreground focus:group-[.destructive]:ring-destructive", className)} {...props} />);
ToastAction.displayName = ToastPrimitives.Action.displayName;
const ToastClose = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Close>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>>(({
  className,
  ...props
}, ref) => <ToastPrimitives.Close ref={ref} className={cn("hidden sm:block absolute cursor-pointer right-1 top-1 rounded-md p-1 text-white/80 opacity-0 transition-opacity hover:text-white focus:opacity-100 focus:outline-hidden focus:ring-1 group-hover:opacity-100 sm:text-foreground/50 sm:hover:text-foreground group-[.destructive]:text-red-300 hover:group-[.destructive]:text-red-50 focus:group-[.destructive]:ring-red-400 focus:group-[.destructive]:ring-offset-red-600", className)} toast-close="" {...props}>
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>);
ToastClose.displayName = ToastPrimitives.Close.displayName;
const ToastTitle = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Title>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>>(({
  className,
  ...props
}, ref) => <ToastPrimitives.Title ref={ref} className={cn("text-sm font-semibold text-white sm:text-foreground dark:sm:text-foreground-strong darkest:sm:text-foreground-strong md:text-lg", className)} {...props} />);
ToastTitle.displayName = ToastPrimitives.Title.displayName;
const ToastDescription = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Description>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>>(({
  className,
  ...props
}, ref) => <ToastPrimitives.Description ref={ref} className={cn("text-sm text-white/90 sm:text-muted-foreground md:text-md opacity-90 leading-tight", className)} {...props} />);
ToastDescription.displayName = ToastPrimitives.Description.displayName;
type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = React.ReactElement<typeof ToastAction>;
export { type ToastProps, type ToastActionElement, ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };